import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ChatSocketService implements OnDestroy {
  private echo!: Echo<any>;
  private channels: Map<string, any> = new Map(); // <-- pour gérer plusieurs rooms
  private messageSubject = new Subject<any>();
  public messages$ = this.messageSubject.asObservable();

  /**
   * Headers d'auth du broadcasting/auth (channels privés). Référence stable :
   * pusher-js relit cet objet à chaque autorisation, on y remet le JWT courant
   * avant chaque souscription (le service peut être créé avant le login).
   */
  private authHeaders: Record<string, string> = { Authorization: '' };

  constructor(private http: HttpClient) {
    (window as any).Pusher = Pusher;

    const REVERB_APP_KEY = (window as any).__env.reverbAppKey || 'test';
    const REVERB_HOST = (window as any).__env.reverbUrl || 'api.narrator.dev.jolifish.eu';
    const REVERB_PATH = REVERB_HOST.includes('staging') || REVERB_HOST.includes('narratheque.io') || REVERB_HOST.includes('narratheque.ca') ? '' : '/reverb';
    // Reverb local (reverbUrl=localhost) : ws brut sur 8090, chemin natif /app
    const IS_LOCAL_REVERB = REVERB_HOST.includes('localhost') || REVERB_HOST.includes('127.0.0.1');
    const REVERB_PORT = IS_LOCAL_REVERB ? 8090 : 443;
    // Endpoint absolu : sous Tauri l'origine est `tauri://localhost`, un
    // `/broadcasting/auth` relatif pointerait au mauvais endroit.
    // La route d'auth broadcasting vit à la RACINE du host (pas sous /api/back) :
    // on en extrait l'origin depuis __env.apiUrl.
    const API_URL = (window as any).__env.apiUrl;
    let broadcastingBase = API_URL;
    try {
      broadcastingBase = new URL(API_URL).origin;
    } catch (_) {
      broadcastingBase = API_URL.replace(/\/api\/back\/?$/, '');
    }

    this.echo = new Echo({
      broadcaster: 'pusher',
      key: REVERB_APP_KEY,
      cluster: 'eu',
      authEndpoint: `${broadcastingBase}/broadcasting/auth`,
      wsHost: REVERB_HOST,
      wsPath: IS_LOCAL_REVERB ? '/app' : REVERB_PATH,
      wsPort: REVERB_PORT,
      wssPort: REVERB_PORT,
      forceTLS: !IS_LOCAL_REVERB,
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
      auth: {
        headers: this.authHeaders,
      }
    });
  }

  private refreshAuthToken(): void {
    const token = localStorage.getItem('access_token');
    this.authHeaders['Authorization'] = token ? `Bearer ${token}` : '';
  }

  private readSubject = new Subject<any>();
  public read$ = this.readSubject.asObservable();

  connect(roomId: string) {
    if (this.channels.has(roomId)) return; // déjà abonné

    this.refreshAuthToken();

    // Channel PRIVÉ (membres du salon) : nom final « private-chat-room.{id} ».
    const channel = this.echo.private(`chat-room.${roomId}`)
      .listen('.message.sent', (event: any) => {
        if (!event.reply_to.id) {
          event.reply_to = null; // Normaliser les replies nulles
        }
        this.messageSubject.next({ ...event, roomId });
      })
      .listen('.message.read', (event: any) => {
        // Événement quand quelqu'un marque comme lu
        this.readSubject.next({ ...event, roomId });
      })
      .error((err: any) => {
        console.error(`❌ Erreur sur channel ${roomId}:`, err);
      });

    this.channels.set(roomId, channel);
  }

  // Méthode pour émettre un "mark as read" (optionnel, si vous voulez aussi émettre)
  markAsRead(roomId: string, lastMessageId: number) {
    // L'API call reste nécessaire pour persister en DB
    // Mais le broadcast se fera automatiquement côté serveur
    return this.http.post(`/api/chat-rooms/${roomId}/mark-as-read`, {
      last_message_id: lastMessageId
    });
  }

  disconnectAll() {
    this.channels.forEach((_, roomId) => {
      this.echo.leave(`chat-room.${roomId}`); // Echo quitte aussi la variante private-
    });
    this.channels.clear();
  }

  ngOnDestroy() {
    this.disconnectAll();
    this.echo.disconnect();
  }
}
